import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ContentPiece } from "../../../shared/types";

// ── Mutations ──

export function usePublishRun(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/publish`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId, "pieces"] });
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
    },
  });
}

export function useSchedulePublish(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { publish_at: string }) =>
      api.post<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/schedule`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId, "pieces"] });
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
    },
  });
}

export function useRetryPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) =>
      api.post<{ data: ContentPiece }>(
        `/api/publishing/pieces/${pieceId}/retry`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}

// ── Queries ──

export function usePublishStatus(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId, "publish-status"],
    queryFn: () =>
      api.get<{ data: ContentPiece[] }>(
        `/api/publishing/run/${runId}/status`,
      ),
    enabled: !!runId,
    refetchInterval: (query) => {
      const pieces = query.state.data?.data;
      if (!pieces) return false;
      const isPublishing = pieces.some(
        (p) => p.publish_status === "publishing",
      );
      return isPublishing ? 5_000 : false;
    },
  });
}
